import { useNavigate } from 'react-router-dom';
import { useSessionStore, type Session } from '@/entities/session';
import type { LoginMfaRequestDto } from '@/features/auth-login';
import { login } from '@/features/auth-login';
import { useRequestStatus } from '@/shared/lib/useRequestStatus';
import { isVerificationCodeInvalidError } from '@/shared/utils';
import type { AxiosError } from 'axios';
import {
  useAppSettingsStore,
  useSessionIdle,
  useWidgetMode,
} from '@/shared/lib';

const useMFA = (resetCode?: () => void) => {
  const navigate = useNavigate();
  const { setSession } = useSessionStore();
  const { previousNavigateUrl, setPreviousNavigateUrl } = useAppSettingsStore();
  const {
    error: mfaError,
    setError: setMfaError,
    isLoading: isMfaLoading,
    setIsLoading: setMfaIsLoading,
  } = useRequestStatus();
  const { checkReturnPageValidity } = useSessionIdle();

  const { isWidget, handleTradingUrlNavigate } = useWidgetMode();

  const runLoginWithMfaCode = async (args: LoginMfaRequestDto) => {
    try {
      setMfaIsLoading(true);
      const mfaLoginResponse = await login<LoginMfaRequestDto>(args, {
        throwOriginalError: true,
      });

      if ('accessToken' in mfaLoginResponse) {
        const data = mfaLoginResponse as Session;
        setSession(data);
        setMfaError(null);

        if (isWidget) {
          handleTradingUrlNavigate();
          return;
        }

        const { isValid, page } = checkReturnPageValidity();

        const url =
          previousNavigateUrl != ''
            ? previousNavigateUrl
            : isValid && page
              ? page
              : '/';

        if (previousNavigateUrl != '') {
          setPreviousNavigateUrl('');
        }

        setTimeout(() => {
          navigate(url);
        }, 100);
      }
    } catch (error) {
      if (isVerificationCodeInvalidError(error)) {
        resetCode?.();
      }
      setMfaError(
        (error as AxiosError)?.message || 'Failed to run MFA process'
      );
    } finally {
      setMfaIsLoading(false);
    }
  };

  return {
    runLoginWithMfaCode,
    isMfaLoading,
    mfaError: isMfaLoading ? null : mfaError,
  };
};

export { useMFA };
